﻿import { useEffect, useMemo, useRef, useState } from 'react';
import useGameStore, { ownershipPct, rentFor, valuationOf } from '../store/gameStore';
import type { MarketPhase, PropertyTile } from '../types/gameTypes';
import { buildPerimeterLayout } from './boardLayout';
import { money } from '../lib/format';
import { PlayerPawn } from './PlayerPawn';
import TileInspector from './TileInspector';
import { TradeSharesModal } from './TradeSharesModal';
import EmergencyModal from './EmergencyModal';
import { TakeoverModal } from './StrategyPanels';
import { AttentionModal } from './TradeModal';
import TradeToast from './TradeToast';
import BotPurchaseToast from './BotPurchaseToast';
import MoneyTransferOverlay from './MoneyTransferOverlay';
import { DISTRICT_META } from './districtMeta';
import { tileName as localizeTile, useT } from '../i18n';
import BoardTable from './BoardTable';
import { CrownIcon, DistrictIcon, SnowIcon, SparkIcon, TileIcon } from './GameIcons';

const STEP_DURATION_MS = 260;
const MAX_ANIMATED_STEPS = 12; // any longer jump (e.g. board reset) snaps instead of walking

const heatValue = (tile: PropertyTile, phase: MarketPhase) => {
  if (tile.tileType === 'COMMERCIAL') {
    const float = tile.totalShares - Object.values(tile.shares).reduce((sum, value) => sum + value, 0);
    return float > 0 ? valuationOf(tile, phase) * 0.2 : rentFor(tile, phase) * 6;
  }
  return tile.tileType === 'TRANSIT' ? 60 : 0;
};

/** One short effect line per tile — the full breakdown lives in the tile inspector. */
const shortEffectOf = (tile: PropertyTile, t: (key: string) => string): string => {
  if (tile.tileType === 'COMMERCIAL') return money(tile.valuation > 0 ? tile.valuation : tile.baseCost);
  if (tile.tileType === 'TRANSIT') return '$60';
  if (tile.tileType === 'FINANCIAL') return t('board.deskShort');
  if (tile.name.includes('Payroll')) return '+$2,000';
  if (tile.name.includes('Audit')) return t('board.taxShort');
  return t('board.specialShort');
};

interface GameBoardProps {
  heatmap: boolean;
}

/** True on compact viewports (phones / portrait tablets — matches the App mobile branch). */
const useCompactViewport = (): boolean => {
  const [compact, setCompact] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)');
    const onChange = () => setCompact(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return compact;
};

const GameBoard = ({ heatmap }: GameBoardProps) => {
  const store = useGameStore();
  const t = useT();
  const compact = useCompactViewport();
  const { board, players, market, currentPlayerIndex, selectedTileIndex, lastRentCredit } = store;

  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [inspectedIndex, setInspectedIndex] = useState<number | null>(null);
  // Closes both the local inspector and the sidebar-driven one.
  const closeInspector = () => {
    setInspectedIndex(null);
    store.setInspectTile(null);
  };
  const [trade, setTrade] = useState<{ tileId: string; mode: 'BUY' | 'SELL' } | null>(null);
  const openTrade = (tileId: string, mode: 'BUY' | 'SELL') => { setInspectedIndex(null); setTrade({ tileId, mode }); };
  const [rentToast, setRentToast] = useState<{ amount: number; from: string; tileIndex: number; key: number } | null>(null);
  const [pulseTile, setPulseTile] = useState<number | null>(null);
  const [cashFlash, setCashFlash] = useState(false);
  // Per-player visual tile index: walks tile by tile behind the store position so
  // a roll reads as a step-by-step animation instead of a teleport.
  const [displayIndex, setDisplayIndex] = useState<Record<string, number>>(() => {
    const initial: Record<string, number> = {};
    players.forEach((player) => {
      initial[player.id] = player.currentTileIndex;
    });
    return initial;
  });

  const active = players[currentPlayerIndex];
  // Board-aware layout: the four corner landmarks stay pinned to the corners even
  // after annexations grow the ring (28 → 30 → 32 tiles …) — no drift, no overlap.
  const layout = useMemo(() => buildPerimeterLayout(board.length, board), [board.length, board]);
  // Group pawns per displayed tile so 10 players sharing one tile stack instead of overflowing.
  const groupedPawns = useMemo(() => {
    const groups = new Map<number, { player: (typeof players)[number]; shown: number; isStepping: boolean }[]>();
    players.forEach((player) => {
      const shown = displayIndex[player.id] ?? player.currentTileIndex;
      const list = groups.get(shown) ?? [];
      list.push({ player, shown, isStepping: shown !== player.currentTileIndex });
      groups.set(shown, list);
    });
    return [...groups.entries()].map(([tileIndex, members]) => ({ tileIndex, members }));
  }, [players, displayIndex]);
  const positionKey = players.map((player) => `${player.id}:${player.currentTileIndex}`).join('|');
  const human = players[0];

  // Rent inflow feedback: any bot landing on human-owned shares pulses the landed
  // tile, floats a cash toast and flashes the portfolio cash readout.
  const seenRentSeq = useRef(0);
  useEffect(() => {
    if (!lastRentCredit || !human || lastRentCredit.seq <= seenRentSeq.current) return;
    seenRentSeq.current = lastRentCredit.seq;
    const myCredit = lastRentCredit.credits.find((c) => c.playerId === human.id);
    if (!myCredit || myCredit.amount <= 0) return;
    setRentToast({ amount: myCredit.amount, from: lastRentCredit.payerName, tileIndex: lastRentCredit.tileIndex, key: lastRentCredit.seq });
    setPulseTile(lastRentCredit.tileIndex);
    setCashFlash(true);
    const t1 = window.setTimeout(() => setRentToast(null), 2600);
    const t2 = window.setTimeout(() => setPulseTile(null), 2000);
    const t3 = window.setTimeout(() => setCashFlash(false), 1400);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [lastRentCredit, human]);

  useEffect(() => {
    const timers: number[] = [];
    let snap: Record<string, number> | null = null;

    players.forEach((player) => {
      const from = displayIndex[player.id];
      if (from === undefined || from === player.currentTileIndex) return;

      const distance = (player.currentTileIndex - from + board.length) % board.length;
      if (distance > MAX_ANIMATED_STEPS) {
        snap = { ...(snap ?? {}), [player.id]: player.currentTileIndex };
        return;
      }

      const next = (from + 1) % board.length;
      timers.push(
        window.setTimeout(() => {
          setDisplayIndex((prev) => ({ ...prev, [player.id]: next }));
        }, STEP_DURATION_MS),
      );
    });

    if (snap) {
      const resolved: Record<string, number> = snap;
      setDisplayIndex((prev) => ({ ...prev, ...resolved }));
    }
    return () => timers.forEach((timer) => window.clearTimeout(timer));
    // players are intentionally read through positionKey/displayIndex to avoid restarting timers
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positionKey, displayIndex, board.length]);

  const activeTileIndex = active ? displayIndex[active.id] ?? active.currentTileIndex : null;
  const focusIndex = hoverIndex ?? inspectedIndex ?? store.inspectTileIndex;

  return (
    <div className="flex h-full max-h-full min-h-0 min-w-0 flex-1 items-center justify-center overflow-hidden">
      <div className="board-shell h-full w-full">
        <div className="board-felt h-full w-full">
          <div className="relative h-full max-h-full min-h-0 w-full max-w-full">
            <div
              className="grid h-full max-h-full w-full"
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${layout.columns}, minmax(0, 1fr))`,
                gridTemplateRows: `repeat(${layout.rows}, minmax(0, 1fr))`,
                width: '100%',
                height: '100%',
                maxHeight: '100%',
              }}
            >
              {board.map((tile, index) => {
                const cell = layout.cells[index] ?? layout.cells[0];
                const isActiveTile = index === activeTileIndex;
                const isSelected = selectedTileIndex === index;
                const heat = heatValue(tile, market.phase);
                const isCorner = cell.isCorner;
                const meta = DISTRICT_META[tile.district];
                const districtColor = meta.bar;
                // Synchronised district glow: hovering or inspecting one tile lights up its whole colour group.
                const focusDistrict = focusIndex !== null ? board[focusIndex]?.district ?? null : null;
                const districtSynced = Boolean(focusDistrict && focusDistrict === tile.district && focusIndex !== index);

                const holders = players
                  .map((player) => ({ player, pct: ownershipPct(tile, player.id) }))
                  .filter((entry) => entry.pct > 0);
                const majority = holders.filter((entry) => entry.pct >= 50).sort((a, b) => b.pct - a.pct)[0];
                const heldPct = holders.reduce((sum, entry) => sum + entry.pct, 0);
                const floatPct = Math.max(0, 100 - heldPct);

                const heatClass = tile.isFrozen
                  ? 'board-tile board-tile-frozen'
                  : heatmap
                    ? heat > 300
                      ? 'board-tile board-tile-hot'
                      : heat > 0
                        ? 'board-tile board-tile-warm'
                        : 'board-tile board-tile-cold'
                    : 'board-tile';

                return (
                  <button
                    key={tile.id}
                    type="button"
                    onMouseEnter={() => setHoverIndex(index)}
                    onFocus={() => setHoverIndex(index)}
                    onMouseLeave={() => setHoverIndex(null)}
                    onBlur={() => setHoverIndex(null)}
                    onClick={() => {
                      if (tile.tileType !== 'COMMERCIAL') {
                        // Special tiles open the interactive "how this tile works" guide.
                        store.setInspectTile(null);
                        store.openTileGuide(tile.id);
                      } else {
                        // Only one inspector may be open: the local one wins, the sidebar one closes.
                        store.setInspectTile(null);
                        setInspectedIndex(index);
                      }
                    }}
                    style={{ gridColumn: cell.column, gridRow: cell.row }}
                    className={`relative cursor-pointer p-0.5 text-left ${compact ? 'min-h-[64px] min-w-[72px]' : 'min-h-[72px] min-w-[80px]'}`}
                  >
                    <div
                      className={`${heatClass} ${isCorner ? 'board-tile-corner' : ''} ${isSelected ? 'ring-2 ring-amber-400' : ''} ${
                        isActiveTile ? 'tile-active-glow' : ''
                      } ${pulseTile === index ? 'rent-pulse' : ''} ${districtSynced ? 'outline outline-2 outline-offset-1' : ''} ${
                        store.spotlightTileId === tile.id ? 'z-10 animate-pulse ring-4 ring-amber-400' : ''
                      }`}
                      style={{
                        borderTopColor: districtColor,
                        outlineColor: districtSynced ? districtColor : undefined,
                      }}
                    >
                      <span className="board-tile-band" style={{ backgroundColor: districtColor }} />
                      {compact ? (
                        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-0.5 overflow-hidden leading-none">
                          <span
                            className="flex h-5 w-5 items-center justify-center rounded-full"
                            style={{ backgroundColor: `${districtColor}22`, color: districtColor }}
                          >
                            <TileIcon tileType={tile.tileType} district={tile.district} size={13} />
                          </span>
                          {tile.upgradeLevel > 0 && (
                            <span className="flex gap-px">
                              {Array.from({ length: tile.upgradeLevel }).map((_, level) => (
                                <span key={level} className="h-1 w-1 rounded-[1px] bg-amber-500" />
                              ))}
                            </span>
                          )}
                          {tile.isFrozen && <SnowIcon size={9} className="text-sky-600" />}
                          {holders.length > 0 && (
                            <div className="flex w-full flex-shrink-0 items-center justify-center gap-px overflow-hidden px-0.5">
                              {holders.slice(0, 4).map((entry) => (
                                <span
                                  key={entry.player.id}
                                  className="h-1 w-1.5 flex-shrink-0 rounded-[1px]"
                                  style={{ backgroundColor: entry.player.color, opacity: majority?.player.id === entry.player.id ? 1 : 0.55 }}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <>
                          <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-0.5 overflow-hidden px-0.5 pt-1">
                            <span
                              className={`flex items-center justify-center rounded-full ${isCorner ? 'h-8 w-8' : 'h-7 w-7'}`}
                              style={{ backgroundColor: `${districtColor}1f`, color: districtColor }}
                            >
                              <TileIcon tileType={tile.tileType} district={tile.district} size={isCorner ? 19 : 16} />
                            </span>
                            <span className="line-clamp-2 w-full break-words text-center text-[9px] font-black leading-[1.15] text-slate-800">
                              {localizeTile(tile.name, t)}
                            </span>
                            <span className={`text-[9px] font-extrabold ${tile.tileType === 'COMMERCIAL' ? 'text-slate-600' : 'text-amber-700'}`}>
                              {shortEffectOf(tile, t)}
                            </span>
                          </div>

                          {tile.tileType === 'COMMERCIAL' ? (
                            <div className="mt-auto w-full space-y-0.5 pb-0.5">
                              <div className="flex h-1.5 w-full overflow-hidden rounded-full bg-slate-300/80">
                                {holders.map((entry) => (
                                  <span
                                    key={entry.player.id}
                                    style={{ width: `${entry.pct}%`, backgroundColor: entry.player.color }}
                                    className="h-full"
                                    title={`${entry.player.name} ${entry.pct.toFixed(0)}%`}
                                  />
                                ))}
                                {floatPct > 0 && <span style={{ width: `${floatPct}%` }} className="h-full bg-slate-400/50" />}
                              </div>
                              <div className="flex h-2.5 items-center justify-center gap-0.5">
                                {majority && (
                                  <span
                                    className="flex items-center gap-0.5 rounded-full px-1 text-[7px] font-black text-slate-900"
                                    style={{ backgroundColor: majority.player.color }}
                                    title={`${majority.player.name} ${Math.round(majority.pct)}%`}
                                  >
                                    <CrownIcon size={7} />
                                    {Math.round(majority.pct)}
                                  </span>
                                )}
                                {tile.upgradeLevel > 0 && (
                                  <span className="flex gap-px">
                                    {Array.from({ length: tile.upgradeLevel }).map((_, level) => (
                                      <span key={level} className="h-1 w-1 rounded-[1px] bg-amber-600" />
                                    ))}
                                  </span>
                                )}
                                {tile.isFrozen && <SnowIcon size={9} className="text-sky-600" />}
                                {tile.isExpansionTile && <SparkIcon size={8} className="text-violet-600" />}
                              </div>
                            </div>
                          ) : (
                            <div className="flex h-2.5 items-center justify-center gap-1 pb-0.5">
                              {tile.isFrozen && <SnowIcon size={9} className="text-sky-600" />}
                              {tile.isExpansionTile && <SparkIcon size={8} className="text-violet-600" />}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </button>
                );
              })}

              {/* Board centre: decision card + company dashboard + live ledger. */}
              <div
                className="hidden min-h-0 lg:block"
                style={{ gridColumn: `2 / ${layout.columns}`, gridRow: `2 / ${layout.rows}` }}
              >
                <BoardTable />
              </div>
            </div>

            {/* Bank / board anchor: destination or origin for tax, payroll and dividends. */}
            <span data-bank-anchor aria-hidden className="pointer-events-none absolute left-1/2 top-1 h-1 w-1 -translate-x-1/2" />

            {/* Flying-cash chips (fixed overlay: travels between ledger cards). */}
            <MoneyTransferOverlay />

            {/* Token layer: players grouped per tile as an overlapping avatar stack
                (mini-grid behavior via wrapping) so 5+ pawns never overflow a tile. */}
            <div className="pointer-events-none absolute inset-0 z-20">
              {groupedPawns.map(({ tileIndex, members }) => {
                const cell = layout.cells[tileIndex] ?? layout.cells[0];
                const crowded = members.length >= 6;
                const busy = members.length >= 4;
                return (
                  <div
                    key={tileIndex}
                    className="absolute flex p-1 transition-[left,top] duration-300 ease-in-out"
                    style={{
                      width: `${100 / layout.columns}%`,
                      height: `${100 / layout.rows}%`,
                      left: `${((cell.column - 1) * 100) / layout.columns}%`,
                      top: `${((cell.row - 1) * 100) / layout.rows}%`,
                      alignItems: 'flex-start',
                      justifyContent: 'center',
                    }}
                  >
                    <div className={`flex max-w-full flex-wrap ${crowded ? '-space-x-2' : busy ? '-space-x-1.5' : '-space-x-1'} ${busy ? 'justify-center' : ''}`}>
                      {members.map(({ player, shown, isStepping }) => (
                        <span
                          key={`${player.id}-${shown}`}
                          title={`${player.name}${player.isBankrupt ? ' (bankrupt)' : ''}${player.id === active?.id ? ' · ACTIVE' : ''}`}
                          className={busy ? 'origin-top scale-[0.82]' : ''}
                        >
                          <PlayerPawn
                            player={player}
                            isActive={player.id === active?.id}
                            isStepping={isStepping}
                          />
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      {inspectedIndex !== null && board[inspectedIndex] && (
        <TileInspector
          tile={board[inspectedIndex]}
          tileIndex={inspectedIndex}
          onClose={closeInspector}
          onBuy={(tile) => openTrade(tile.id, 'BUY')}
          onSell={(tile) => openTrade(tile.id, 'SELL')}
        />
      )}
      {store.inspectTileIndex !== null && board[store.inspectTileIndex] && (
        <TileInspector
          tile={board[store.inspectTileIndex]}
          tileIndex={store.inspectTileIndex}
          onClose={closeInspector}
          onBuy={(tile) => openTrade(tile.id, 'BUY')}
          onSell={(tile) => openTrade(tile.id, 'SELL')}
        />
      )}
      {trade && <TradeSharesModal tile={board.find((tile) => tile.id === trade.tileId) ?? null} mode={trade.mode} onClose={() => setTrade(null)} />}
      <EmergencyModal />
      <TakeoverModal />
      <AttentionModal />
      <TradeToast />
      <BotPurchaseToast />
      {rentToast && (
        <div key={rentToast.key} className="rent-toast pointer-events-none fixed left-1/2 top-6 z-[70] -translate-x-1/2">
          <div className="rent-toast-inner">
            <div className="rent-toast-glow flex items-center gap-2.5 rounded-full border border-emerald-400/70 bg-gradient-to-r from-emerald-500/25 to-teal-500/15 px-5 py-2.5 text-sm font-extrabold text-emerald-100 backdrop-blur">
              <span aria-hidden className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-400/25 text-emerald-100 shadow-[0_0_12px_rgba(52,211,153,0.6)]">
                <DistrictIcon district="FINANCIAL" size={15} />
              </span>
              <span>
                <span className="text-emerald-300">+{money(rentToast.amount)}</span> {t('board.rentToast', { name: rentToast.from })}
              </span>
            </div>
          </div>
        </div>
      )}
      {cashFlash && human && (
        <div className="pointer-events-none fixed bottom-6 right-6 z-[70]">
          <div className="cash-flash rounded-2xl border border-emerald-400/60 bg-slate-950/90 px-4 py-2 text-sm font-extrabold">
            {money(human.cash)}
          </div>
        </div>
      )}
    </div>
  );
};

export default GameBoard;
